import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Song } from '../types/models';
import { usePlayer } from '../lib/PlayerContext';
import { Typography } from './ui/Typography';

interface LRCLine {
  time: number;
  text: string;
  id: string;
}

interface ParticleProps {
  x: number;
  y: number;
  color: string;
  size: number;
  velocity: { x: number; y: number };
  opacity: number;
  life: number;
}

interface LyricsOverlayProps {
  song: Song;
  currentTime: number;
  isVisible: boolean;
  onClose: () => void;
}

export function LyricsOverlay({ song, currentTime, isVisible, onClose }: LyricsOverlayProps) {
  const [lyrics, setLyrics] = useState<LRCLine[]>([]);
  const [currentLine, setCurrentLine] = useState<number>(-1);
  const [isEntering, setIsEntering] = useState(false);
  const [particles, setParticles] = useState<ParticleProps[]>([]);
  const [backgroundShift, setBackgroundShift] = useState(0);
  const [lyricOpacity, setLyricOpacity] = useState(1);
  
  const lyricsContainerRef = useRef<HTMLDivElement>(null);
  const currentLineRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const { seek } = usePlayer();

  // Particle system for magical effects
  const createParticles = useCallback((count: number = 20) => {
    const newParticles: ParticleProps[] = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        color: Math.random() > 0.5 ? '#FF4D8C' : '#4DE8FF',
        size: Math.random() * 3 + 1,
        velocity: {
          x: (Math.random() - 0.5) * 2,
          y: (Math.random() - 0.5) * 2
        },
        opacity: Math.random() * 0.8 + 0.2,
        life: Math.random() * 100 + 50
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
  }, []);

  // Animate particles
  const animateParticles = useCallback(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    setParticles(prev => {
      const updated = prev.map(particle => ({
        ...particle,
        x: particle.x + particle.velocity.x,
        y: particle.y + particle.velocity.y,
        life: particle.life - 1,
        opacity: particle.opacity * 0.99
      })).filter(particle => particle.life > 0 && particle.opacity > 0.01);

      // Draw particles
      updated.forEach(particle => {
        ctx.save();
        ctx.globalAlpha = particle.opacity;
        ctx.fillStyle = particle.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      return updated;
    });

    animationFrameRef.current = requestAnimationFrame(animateParticles);
  }, []);

  // Entry animation effect
  useEffect(() => {
    if (isVisible) {
      setIsEntering(true);
      createParticles(50);
      animateParticles();
      
      setTimeout(() => setIsEntering(false), 1000);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setParticles([]);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible, createParticles, animateParticles]);

  // Background shift animation
  useEffect(() => {
    const interval = setInterval(() => {
      setBackgroundShift(prev => (prev + 0.5) % 360);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Lyric fade effect on line changes
  useEffect(() => {
    setLyricOpacity(0);
    const timer = setTimeout(() => setLyricOpacity(1), 150);
    return () => clearTimeout(timer);
  }, [currentLine]);

  useEffect(() => {
    if (!song.lrcUrl) {
      console.log('No LRC URL found for song:', song.title);
      return;
    }

    console.log('Fetching lyrics from:', song.lrcUrl);
    
    // Use the proxy endpoint to avoid CORS issues
    const encodedUrl = encodeURIComponent(song.lrcUrl);
    const proxyUrl = `/api/lyrics/${encodedUrl}`;
    
    console.log('Using proxy URL:', proxyUrl);
    
    fetch(proxyUrl)
      .then(res => {
        console.log('LRC fetch response status:', res.status);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.text();
      })
      .then(text => {
        console.log('LRC text length:', text.length);
        console.log('LRC text preview:', text.substring(0, 200));
        const lines = parseLRC(text);
        console.log('Parsed lyrics lines:', lines.length);
        console.log('First few lines:', lines.slice(0, 3));
        setLyrics(lines);
        
        // Create celebration particles when lyrics load
        if (lines.length > 0) {
          createParticles(30);
        }
      })
      .catch(error => {
        console.error('Error fetching lyrics:', error);
      });
  }, [song.lrcUrl, createParticles]);

  useEffect(() => {
    if (!lyrics.length) return;

    // Add a small negative offset to make lyrics appear slightly ahead for better readability
    const adjustedTime = currentTime + 0.2; // 200ms ahead for better sync

    const lineIndex = lyrics.findIndex((line, index) => {
      const nextLine = lyrics[index + 1];
      return line.time <= adjustedTime && (!nextLine || nextLine.time > adjustedTime);
    });

    if (lineIndex !== currentLine) {
      setCurrentLine(lineIndex);
      
      // Create magical particles on line change
      if (lineIndex >= 0) {
        createParticles(15);
      }

      // Smooth scroll with enhanced easing
      if (currentLineRef.current && lyricsContainerRef.current) {
        const container = lyricsContainerRef.current;
        const element = currentLineRef.current;
        const containerHeight = container.clientHeight;
        const elementTop = element.offsetTop;
        const elementHeight = element.offsetHeight;

        // Smooth scroll with custom easing
        container.scrollTo({
          top: elementTop - (containerHeight / 2) + (elementHeight / 2),
          behavior: 'smooth'
        });
      }
    }
  }, [currentTime, lyrics, currentLine, createParticles]);

  const handleLyricClick = useCallback((time: number) => {
    const progress = time / song.duration;
    const clampedProgress = Math.max(0, Math.min(1, progress));
    seek(clampedProgress);
    
    // Create celebration particles on seek
    createParticles(25);
  }, [song.duration, seek, createParticles]);

  // Memoized line styles for performance
  const getLineStyles = useMemo(() => (index: number) => {
    const isActive = index === currentLine;
    const isAdjacent = index === currentLine - 1 || index === currentLine + 1;
    const isNear = Math.abs(index - currentLine) <= 2;
    
    return {
      base: "block w-full text-center cursor-pointer transform-gpu transition-all duration-700 ease-out-quart",
      active: isActive 
        ? "text-white text-4xl md:text-5xl font-bold scale-110" 
        : "",
      adjacent: isAdjacent && !isActive 
        ? "text-pearl-white/90 text-2xl md:text-3xl font-medium scale-105" 
        : "",
      near: isNear && !isActive && !isAdjacent 
        ? "text-slate-grey/80 text-xl font-normal scale-100" 
        : "",
      far: !isNear 
        ? "text-slate-grey/40 text-lg font-light scale-95 opacity-60" 
        : "",
      hover: "hover:text-naia-pink hover:scale-105 transition-all duration-300",
      interactive: "hover:animate-float focus:outline-none focus:ring-2 focus:ring-naia-pink/50 focus:ring-offset-2 focus:ring-offset-deep-night"
    };
  }, [currentLine]);

  if (!isVisible) {
    return null;
  }
  
  if (!song.lrcUrl) {
    return null;
  }

  return (
    <div className={`
      fixed inset-0 z-50 flex flex-col overflow-hidden
      bg-gradient-to-br from-deep-night via-surface-dark to-true-black
      backdrop-blur-2xl
      ${isEntering ? 'animate-slide-in-bottom' : 'animate-fade-in'}
    `}>
      {/* Animated Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{ 
          background: `linear-gradient(${backgroundShift}deg, 
            rgba(255, 77, 140, 0.03) 0%, 
            rgba(77, 232, 255, 0.02) 50%, 
            rgba(156, 107, 152, 0.03) 100%)`
        }}
      />
      
      {/* Dynamic Background Effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-naia-pink/10 rounded-full filter blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-accent/10 rounded-full filter blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-soft-violet/10 rounded-full filter blur-2xl animate-pulse"></div>
      </div>

      {/* Enhanced Header */}
      <header className="relative z-10 p-8 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center max-w-4xl mx-auto">
          <div className="space-y-2">
            <Typography variant="h2" className="animate-slide-in-left">
              {song.title}
            </Typography>
          </div>
          
          <button
            onClick={onClose}
            className="group relative p-4 rounded-2xl bg-white/5 hover:bg-white/10 
                     border border-white/10 hover:border-naia-pink/30
                     backdrop-blur-xl transition-all duration-300 
                     hover:scale-110 hover:shadow-naia-glow
                     focus:outline-none focus:ring-2 focus:ring-naia-pink/50"
          >
            <svg 
              className="w-6 h-6 text-slate-grey group-hover:text-naia-pink transition-colors duration-300" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            
            {/* Hover effect ring */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-naia-pink/20 to-cyan-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>
          </button>
        </div>
      </header>
      
      {/* Main Lyrics Container */}
      <main className="flex-1 relative overflow-hidden">
        <div 
          ref={lyricsContainerRef}
          className="h-full overflow-y-auto scrollbar-hide select-none relative z-10"
          style={{
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
            scrollBehavior: 'smooth'
          }}
        >
          <div className="py-[50vh] px-8">
            <div className="max-w-4xl mx-auto space-y-12">
              {lyrics.length === 0 ? (
                <div className="text-center py-20 animate-pulse">
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-naia-pink to-cyan-accent animate-spin-slow"></div>
                    <Typography variant="h3" color="muted" className="animate-bounce">
                      {song.lrcUrl ? 'Loading magical lyrics...' : 'No lyrics available'}
                    </Typography>
                    <div className="flex justify-center space-x-2 mt-6">
                      <div className="w-2 h-2 bg-naia-pink rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-cyan-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-soft-violet rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              ) : (
                lyrics.map((line, index) => {
                  const styles = getLineStyles(index);
                  return (
                    <button
                      key={line.id}
                      ref={index === currentLine ? currentLineRef : undefined}
                      onClick={() => handleLyricClick(line.time)}
                      className={`
                        ${styles.base}
                        ${styles.active}
                        ${styles.adjacent}
                        ${styles.near}
                        ${styles.far}
                        ${styles.hover}
                        ${styles.interactive}
                      `}
                      style={{
                        opacity: lyricOpacity,
                        transitionDelay: `${index * 50}ms`,
                        transform: index === currentLine 
                          ? 'translateY(0) scale(1.1)' 
                          : 'translateY(0) scale(1)',
                        textShadow: 'none'
                      }}
                    >
                      <span className="inline-block leading-relaxed px-4 py-2 rounded-lg">
                        {line.text || '\u00A0'}
                      </span>
                      
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        
      </main>
    </div>
  );
}

function parseLRC(lrc: string): LRCLine[] {
  const lines = lrc.split('\n');
  const timeRegex = /\[(\d{1,2}):(\d{2})\.(\d{2,3})\]/; // Allow 1-2 digits for minutes
  
  console.log('Parsing LRC with', lines.length, 'lines');
  
  const parsedLines = lines
    .map((line, index) => {
      const match = timeRegex.exec(line);
      if (!match) {
        // Also try alternative formats like [mm:ss.xxx] or [mm:ss:xxx]
        const altRegex = /\[(\d{1,2}):(\d{2})[:\.](\d{2,3})\]/;
        const altMatch = altRegex.exec(line);
        if (!altMatch) {
          if (line.trim() && !line.startsWith('[')) {
            console.log('Could not parse line', index + 1, ':', line);
          }
          return null;
        }
        const minutes = parseInt(altMatch[1], 10);
        const seconds = parseInt(altMatch[2], 10);
        const milliseconds = parseInt(altMatch[3].padEnd(3, '0'), 10);
        
        const time = minutes * 60 + seconds + milliseconds / 1000;
        const text = line.replace(altRegex, '').trim();
        
        return { 
          time, 
          text, 
          id: `lyric-${time}-${index}` 
        };
      }
      
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);
      const milliseconds = parseInt(match[3].padEnd(3, '0'), 10);
      
      const time = minutes * 60 + seconds + milliseconds / 1000;
      const text = line.replace(timeRegex, '').trim();
      
      return { 
        time, 
        text, 
        id: `lyric-${time}-${index}` 
      };
    })
    .filter((line): line is LRCLine => line !== null)
    .sort((a, b) => a.time - b.time);
    
  console.log('Successfully parsed', parsedLines.length, 'lyrics lines');
  return parsedLines;
} 